import { faChevronDown, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    ButtonLabel,
    ButtonLabelIcon,
    ButtonLabelText,
    ClearButton,
    DropdownButton,
    DropdownPaddingOverride,
} from 'components/dropdown/styled';
import { CSSProperties, forwardRef, ReactNode, Ref, useRef } from 'react';
import { stopEvent } from 'utils/event';
import { mergeRefs } from 'utils/mergeRefs';
import { MouseOrKeyboardEvent } from 'utils/types';

export type DropdownTriggerProps = React.DetailedHTMLProps<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
> &
    Readonly<{
        /**
         * Hides the chevron icon.
         */
        hideChevron?: boolean;
        /**
         * The aria label.
         */
        ariaLabel?: string;
        children?: ReactNode;
        paddingOverride?: DropdownPaddingOverride;
        chevronColor?: string;
        style?: CSSProperties;
        /**
         * When supplied, shows a clear button with this callback as the onClick action.
         */
        onClear?: () => void;
        /**
         * Whether the trigger is currently showing placeholder text (no value selected).
         */
        isPlaceholder?: boolean;
    }>;

const DropdownTriggerImpl = (
    {
        hideChevron,
        children,
        ariaLabel,
        paddingOverride,
        chevronColor,
        style,
        onClear,
        isPlaceholder,
        ...rest
    }: DropdownTriggerProps,
    ref: Ref<HTMLButtonElement>,
) => {
    const refObj = useRef<HTMLButtonElement>(null);

    const onClearKey = (event: React.KeyboardEvent) => {
        if (event.code === 'Enter' || event.code === 'Space') {
            stopEvent(event);
            onClear?.();
            refObj.current?.focus();
        }
    };

    const onClearClick = (event: MouseOrKeyboardEvent) => {
        stopEvent(event);
        onClear?.();
        refObj.current?.focus();
    };

    return (
        <DropdownButton
            type="button"
            {...rest}
            ref={mergeRefs(ref, refObj)}
            aria-label={ariaLabel}
            $paddingOverride={paddingOverride}
            style={style}
        >
            <ButtonLabel>
                <ButtonLabelText
                    className="ellipsis-1-line"
                    $isPlaceholder={isPlaceholder}
                >
                    {children}
                </ButtonLabelText>
                {onClear && (
                    <ClearButton
                        role="button"
                        tabIndex={0}
                        onKeyDown={onClearKey}
                        onPointerDown={stopEvent}
                        onClick={onClearClick}
                    >
                        <FontAwesomeIcon icon={faXmark} color={chevronColor} />
                    </ClearButton>
                )}
                {!hideChevron && (
                    <ButtonLabelIcon
                        icon={faChevronDown}
                        color={chevronColor}
                        $reduceLeftPadding={onClear !== undefined}
                    />
                )}
            </ButtonLabel>
        </DropdownButton>
    );
};

export const DropdownTrigger = forwardRef(DropdownTriggerImpl);
